import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, X, Trash2, Award, LogOut, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewAdminPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved

  const { getAuthHeaders, logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/admin`, {
        headers: getAuthHeaders()
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateReview = async (reviewId, updates) => {
    try {
      await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}`, updates, {
        headers: getAuthHeaders()
      });
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Weet je zeker dat je deze review wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/reviews/${reviewId}`, {
        headers: getAuthHeaders()
      });
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'pending') return !review.is_approved;
    if (filter === 'approved') return review.is_approved;
    return true;
  });

  const pendingCount = reviews.filter(r => !r.is_approved).length;
  const approvedCount = reviews.filter(r => r.is_approved).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#f8f9fc] pt-32 pb-20" data-testid="review-admin-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a]">Review Beheer</h1>
            <p className="text-[#475569] mt-1">Welkom, {user} • Beheer klantreviews</p>
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
          >
            <LogOut size={18} />
            Uitloggen
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-[#475569]">Totaal</p>
            <p className="text-2xl font-bold text-[#0f172a]">{reviews.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-[#475569]">Wachtend</p>
            <p className="text-2xl font-bold text-orange-500">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-[#475569]">Goedgekeurd</p>
            <p className="text-2xl font-bold text-green-500">{approvedCount}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'all', label: 'Alle', count: reviews.length },
            { id: 'pending', label: 'Wachtend', count: pendingCount },
            { id: 'approved', label: 'Goedgekeurd', count: approvedCount },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                filter === tab.id
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-white text-[#475569] hover:bg-slate-100'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          <AnimatePresence>
            {filteredReviews.map((review) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`bg-white rounded-xl p-6 shadow-sm border-l-4 ${
                  review.is_approved ? 'border-green-500' : 'border-orange-500'
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-[#0f172a]">{review.name}</p>
                      <p className="text-sm text-[#475569]">
                        {review.company && `${review.company} • `}
                        {review.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}
                      />
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="mb-4">
                  <h3 className="font-semibold text-[#0f172a] mb-2">"{review.title}"</h3>
                  <p className="text-[#475569]">{review.content}</p>
                </div>

                {/* Meta */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-4 text-sm text-[#475569]">
                    {review.project_type && (
                      <span className="px-2 py-1 bg-slate-100 rounded-lg">{review.project_type}</span>
                    )}
                    <span>{new Date(review.created_at).toLocaleDateString('nl-NL')}</span>
                    {review.is_featured && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-lg flex items-center gap-1">
                        <Award size={14} /> Uitgelicht
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!review.is_approved ? (
                      <button
                        onClick={() => updateReview(review.id, { is_approved: true })}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-1"
                        title="Goedkeuren"
                      >
                        <Check size={16} />
                        Goedkeuren
                      </button>
                    ) : (
                      <button
                        onClick={() => updateReview(review.id, { is_approved: false })}
                        className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center gap-1"
                        title="Verbergen"
                      >
                        <X size={16} />
                        Verbergen
                      </button>
                    )}
                    <button
                      onClick={() => updateReview(review.id, { is_featured: !review.is_featured })}
                      className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
                        review.is_featured
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title={review.is_featured ? 'Niet meer uitlichten' : 'Uitlichten'}
                    >
                      <Award size={16} />
                    </button>
                    <button
                      onClick={() => deleteReview(review.id)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredReviews.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl">
              <p className="text-[#475569]">Geen reviews gevonden</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ReviewAdminPage;
